import { useCallback, useEffect, useRef, useState } from 'react';
import {
  generateConnectionUrl,
  getConnectionStatus,
  checkAndCleanupProfile,
  SocialConnection,
} from '../api/ayrshareApi';

/** What came of a connect attempt, once the window closed / the tab came back. */
export interface ConnectOutcome {
  /** The channel the caller was trying to link, if it said. */
  platform: string | null;
  /** That channel (or, unspecified, anything) now reads linked and healthy. */
  linked: boolean;
  at: number;
}

const SETTLE_POLL_MS = 3000;
const SETTLE_TRIES = 7; // ≈ 20 s — the link webhook / Ayrshare state can lag the redirect
const IGNORE_RETURN_WITHIN_MS = 1500; // focus bounces the instant the popup opens

/**
 * The Ayrshare connect flow, shared by every connect affordance (Editorial
 * channel rows, Tended/Published headers, the publish button). Opens the
 * all-platforms linking page — Ayrshare has no per-platform deep link in
 * our flow — then CONFIRMS the result instead of going quiet:
 *
 *  - settles when the popup closes (desktop) OR when this tab comes back
 *    into view / focus (mobile opens a new tab and the OAuth hop into the
 *    network's app may never "close" anything);
 *  - polls connection status for ~20 s, because the link lands a beat
 *    after the redirect;
 *  - reports `outcome` — linked, or not — so the caller can say
 *    "TikTok linked ✓" or "didn't link, try again" rather than nothing.
 *
 * `onConnections` receives every refreshed list so callers update their
 * own state; a zero-connection profile still gets the orphan cleanup.
 */
export function useAyrshareConnect(
  brandId: number | undefined,
  token?: string,
  onConnections?: (connections: SocialConnection[]) => void
) {
  const [connecting, setConnecting] = useState(false);
  const [outcome, setOutcome] = useState<ConnectOutcome | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => { cleanupRef.current?.(); }, []);

  // Callable as `connect('tiktok')` or straight as an onClick handler (the
  // event is not a platform).
  const connect = useCallback(async (platformOrEvent?: string | unknown) => {
    const platform = typeof platformOrEvent === 'string' ? platformOrEvent : undefined;
    if (!brandId || connecting) return;
    setConnecting(true);
    setOutcome(null);
    try {
      const response = await generateConnectionUrl(brandId, token);
      if (response.status !== 'success' || !response.url) {
        throw new Error(response.error || 'Failed to generate connection URL');
      }
      const width = 800;
      const height = 700;
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      const popup = window.open(
        response.url,
        'Connect Social Accounts',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      const openedAt = Date.now();
      let settled = false;

      const isLinked = (list: SocialConnection[]) =>
        platform
          ? list.some((c) => c.platform === platform && c.is_connected && !c.needs_reconnect)
          : list.some((c) => c.is_connected);

      const settle = async () => {
        if (settled) return;
        settled = true;
        teardown();
        let latest: SocialConnection[] = [];
        let linked = false;
        try {
          for (let i = 0; i < SETTLE_TRIES; i++) {
            const res = await getConnectionStatus(brandId, token);
            latest = res.connections || [];
            onConnections?.(latest);
            if (isLinked(latest)) { linked = true; break; }
            await new Promise((r) => setTimeout(r, SETTLE_POLL_MS));
          }
          if (!latest.some((c) => c.is_connected)) {
            try {
              await checkAndCleanupProfile(brandId, true, token);
            } catch (cleanupErr) {
              console.warn('[AyrshareConnect] Profile cleanup failed:', cleanupErr);
            }
          }
        } catch (err) {
          console.error('[AyrshareConnect] Status refresh failed:', err);
        } finally {
          setOutcome({ platform: platform ?? null, linked, at: Date.now() });
          setConnecting(false);
        }
      };

      const onReturn = () => {
        // The popup's own open steals focus for an instant; a return that
        // early is not the user coming back.
        if (Date.now() - openedAt < IGNORE_RETURN_WITHIN_MS) return;
        if (document.visibilityState === 'visible') settle();
      };
      const closedPoll = window.setInterval(() => {
        // Only a popup we can see close settles here; a null handle
        // (mobile tab, blocker) settles on the tab's return or the give-up.
        if (popup && popup.closed) settle();
      }, 1000);
      const giveUp = window.setTimeout(settle, 10 * 60 * 1000);
      document.addEventListener('visibilitychange', onReturn);
      window.addEventListener('focus', onReturn);
      const teardown = () => {
        window.clearInterval(closedPoll);
        window.clearTimeout(giveUp);
        document.removeEventListener('visibilitychange', onReturn);
        window.removeEventListener('focus', onReturn);
        cleanupRef.current = null;
      };
      cleanupRef.current = teardown;
      if (!popup) {
        // Blocked or opened as a tab we can't see: the return events carry it
        console.warn('[AyrshareConnect] window.open returned null — waiting for the tab to come back');
      }
    } catch (error) {
      console.error('[AyrshareConnect] Connect failed:', error);
      setConnecting(false);
    }
  }, [brandId, token, connecting, onConnections]);

  const clearOutcome = useCallback(() => setOutcome(null), []);

  return { connect, connecting, outcome, clearOutcome };
}
